import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import * as moment from 'moment';
import { debounceTime, Subject } from 'rxjs';
import { ScrumService } from 'src/app/_services/scrum.service';
import { BaseComponent } from '../../../base/base.component';
import { ScrumComponent } from '../scrum.component';

@Component({
  selector: 'app-scrum-billing',
  templateUrl: './scrum-billing.component.html',
  styleUrls: ['./scrum-billing.component.css']
})
export class ScrumBillingComponent extends BaseComponent {
  @Input() data: any;
  @Input() sprints: any[];
  @Input() selectedSprint: any;
  @Input() totalCapacity: any;
  @Input() currentIndex: number;
  @Input() is_authorized: boolean;

  @Output() sprintIdEmit: EventEmitter<number> = new EventEmitter<number>();

  value: number;

  highlightedUser: number | null = null;
  isMobile = window.innerWidth <= 767;
  isLoading: boolean = false;

  focusDay: any;
  firstKeyrow: any;

  filterSprint: number;
  sprint_id: number;
  selectedSprintIndex: number = 0;
  residuePbi: any = [];

  currentDay = moment().format('YYYY-MM-DD');
  errorMessage: string | null = null;

  private readonly cellValueChangeCapacityUsers = new Subject<{
    capacity_id: number;
    value: number;
    billing_value: number;
  }>();
  private readonly scrumService: ScrumService = inject(ScrumService);

  constructor(private readonly scrum: ScrumComponent) {
    super();
  }

  ngOnInit() {
    this.selectedSprintIndex = this.sprint_id - 1; // Funzione da implementare per ottenere l'indice dello sprint corrente

    if (this.selectedSprintIndex <= 0) this.selectedSprintIndex = 0;
    else this.selectedSprintIndex - 1;

    // cambiamento dei valori degli input della capacity
    this.cellValueChangeCapacityUsers
      .pipe(
        debounceTime(750) // Ritarda di 100 millisecondi
      )
      .subscribe(({ capacity_id, billing_value }) => {
        this.isLoading = true;
        this.updateCapacitySprint(capacity_id, billing_value);
      });
  }

  /**
   * Funzione che serve per gestire l'inserimento da tastiera, limitando ai numeri
   * @param event cattura l'evento da tastiera
   */
  blockInvalidKeys(event: KeyboardEvent): void {
    const isNumber = /^\d$/.test(event.key);
    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Tab',
      '.',
      ','
    ];

    if (!isNumber && !allowedKeys.includes(event.key)) event.preventDefault();
  }

  updateCapacitySprint(capacity_id: number, billing_value: any) {
    if (billing_value > 8) billing_value = 8;

    if (billing_value != null) {
      this.scrumService
        .updateCapacitySprint(
          {
            capacity_id: capacity_id,
            sprint_id: this.selectedSprint.id,
            value: this.value,
            billing_value: billing_value
          },
          capacity_id
        )
        .subscribe({
          next: res => {
            if (!res) return;
            this.highlightedUser = null;
            this.errorMessage = null;
            this.isLoading = false;

            // Gestione di warning non bloccante
            if (res.warning) {
              this.responseToast('warning', res.warning);
            } else {
              this.responseToast('success', res.message);
            }
            this.scrum.getInitializationData();
          }
        });
    }
  }

  /**
   * Funzione per mettere in evidenza la riga di un utente
   * @param userId id dell'utente
   */
  highlightUser(userId: number) {
    this.highlightedUser = userId;

    document.addEventListener('click', this.handleOutsideClick.bind(this));
  }

  /**
   * Funzione per rimuovere l'evidenza dalla riga di un utente
   */
  removeHighlight() {
    this.highlightedUser = null;

    document.removeEventListener('click', this.handleOutsideClick.bind(this));
  }

  /**
   * Gestore per i click esterni
   * @param event evento di click
   */
  private handleOutsideClick(event: MouseEvent) {
    const clickedInsideTable = (event.target as HTMLElement).closest(
      '.highlight'
    );

    if (!clickedInsideTable) this.removeHighlight();
  }

  /**
   * Inviamo il valore al Subject per attivare il debouncing
   * @param capacity_id
   * @param value
   */
  cellValueChangedCapacityForUsers(
    capacity_id: number,
    value: number,
    billing_value: number
  ) {
    this.value = value;

    if (billing_value != null) {
      this.cellValueChangeCapacityUsers.next({
        capacity_id,
        value,
        billing_value
      });
    }
  }

  /**
   * Funzione per verificare se il pulsante di sinistra deve essere disabilitato
   */
  isLeftButtonDisabled(): boolean {
    return this.currentIndex === 0;
  }

  /**
   * Funzione per verificare se il pulsante di destra deve essere disabilitato
   */
  isRightButtonDisabled(): boolean {
    return this.currentIndex === this.sprints.length - 1;
  }

  /**
   * Funzione per filtrare i sprints
   */
  filteredSprints(event?: any) {
    if (event) {
      this.filterSprint = event;
      if (!this.filterSprint) {
        return [...this.sprints].reverse();
      } else {
        return this.sprints.filter(
          sprint => sprint.number === this.filterSprint
        );
      }
    } else return [...this.sprints].reverse();
  }

  /**
   * Funzione per cambiare lo sprint da visualizzare
   * @param sprint
   */
  changeSprint(sprint: any) {
    this.sprint_id = sprint.id;
    this.selectedSprintIndex = this.sprints.indexOf(sprint);
    this.sprintIdEmit.emit(this.sprint_id);
  }

  /**
   * Funzione per selezionare lo sprint precedente
   */
  selectPreviousSprint() {
    const filtered = this.sprints;
    if (filtered.length > 0) {
      this.currentIndex =
        (this.currentIndex - 1 + filtered.length) % filtered.length;
      this.selectedSprint = filtered[this.currentIndex];
      this.sprint_id = this.selectedSprint.id;
      this.sprintIdEmit.emit(this.sprint_id);
    }
  }

  /**
   * Funzione per selezionare lo sprint successivo
   */
  selectNextSprint() {
    const filtered = this.sprints;
    if (filtered.length > 0) {
      this.currentIndex = (this.currentIndex + 1) % filtered.length;
      this.selectedSprint = filtered[this.currentIndex];
      this.sprint_id = this.selectedSprint.id;
      this.sprintIdEmit.emit(this.sprint_id);
    }
  }

  isUserHighlighted(user: any): boolean {
    if (!user.project_user?.length) return false;

    const today = new Date(); // Data attuale

    // Trova l'ultimo periodo terminato PRIMA di oggi (se esiste)
    const latestEndedPeriod = user.project_user
      .filter(
        (p: { endwork_date: string | number | Date }) =>
          p.endwork_date && new Date(p.endwork_date) < today
      )
      .reduce(
        (latest: string | null, p: { endwork_date: string | number | Date }) =>
          !latest || new Date(p.endwork_date) > new Date(latest)
            ? p.endwork_date
            : latest,
        null
      );

    // Controlla se l'utente ha attualmente un periodo lavorativo per quella sprint
    const isCurrentlyWorking = user.project_user.some(
      (p: {
        startwork_date: string | number | Date;
        endwork_date: string | number | Date;
      }) =>
        new Date(p.startwork_date) <= today &&
        (!p.endwork_date || new Date(p.endwork_date) >= today) // Se endwork_date è null, significa che è ancora in corso
    );

    // Evidenzia solo se l'utente HA terminato una collab ma NON ne ha attive attualmente
    return latestEndedPeriod !== null && !isCurrentlyWorking;
  }

  getEndWorkDate(user: any): string {
    if (!user.project_user || user.project_user.length === 0) return '';

    // Trova il `project_user` più recente (ordinato per `startwork_date`)
    const lastProjectUser = [...user.project_user].sort(
      (a, b) =>
        new Date(b.startwork_date).getTime() -
        new Date(a.startwork_date).getTime()
    )[0];

    // Se ha una `endwork_date`, la restituisce come tooltip
    return lastProjectUser?.endwork_date
      ? `Data fine collaborazione: ${new Date(
          lastProjectUser.endwork_date
        ).toLocaleDateString('it-IT')}`
      : '';
  }

  endWorkDate(user: any, date: string): boolean {
    if (!user.project_user?.length) return false; // Se non ha project_user, non bloccare nulla

    const dateToCheck = new Date(date);

    // Trova il periodo lavorativo più recente **prima della data corrente**
    const latestEndedPeriod = user.project_user
      .filter(
        (p: { endwork_date: string | number | Date }) =>
          p.endwork_date && new Date(p.endwork_date) <= dateToCheck
      )
      .reduce(
        (latest: string | null, p: { endwork_date: string | number | Date }) =>
          !latest || new Date(p.endwork_date) > new Date(latest)
            ? p.endwork_date
            : latest,
        null
      );

    // Trova il primo periodo lavorativo **dopo la data corrente**
    const nextStartPeriod = user.project_user
      .filter(
        (p: { startwork_date: string | number | Date }) =>
          new Date(p.startwork_date) > dateToCheck
      )
      .reduce(
        (
          earliest: string | null,
          p: { startwork_date: string | number | Date }
        ) =>
          !earliest || new Date(p.startwork_date) < new Date(earliest)
            ? p.startwork_date
            : earliest,
        null
      );

    // Controlla se l'utente ha un nuovo periodo attivo che inizia successivamente alla data corrente
    const hasFutureStart = user.project_user.some(
      (p: {
        startwork_date: string | number | Date;
        endwork_date: string | number | Date;
      }) =>
        new Date(p.startwork_date) <= dateToCheck &&
        (!p.endwork_date || new Date(p.endwork_date) >= dateToCheck)
    );

    // Se c'è un `startwork_date` futuro, blocca i giorni **prima** di questa data
    if (nextStartPeriod && dateToCheck < new Date(nextStartPeriod)) {
      return true;
    }

    // Disabilita le celle solo se **non** c'è un nuovo periodo attivo
    return latestEndedPeriod
      ? !hasFutureStart && dateToCheck > new Date(latestEndedPeriod)
      : false;
  }
}
